import { formatISO } from "date-fns";
import { isLengthUnit, isVolumeFlowRateUnit, Units } from "../../convert";
import { TabularData } from "../../models/SensorBase";

export interface TabularApiResponseMeta {
  units: { [measure: string]: Units };
}
export interface TabularApiResponseData {
  ts: string;
  [measure: string]: any;
}

export interface TabularApiResponse {
  meta: TabularApiResponseMeta;
  data: TabularApiResponseData[];
}

export const rootApiUrl = process.env.REACT_APP_API_URL || "https://jefdaq.jefulleralert.com";

const fetchTabular = async (
  type: string,
  id: string,
  startDate: Date,
  endDate: Date
): Promise<TabularData> => {

  const search = new URLSearchParams({
    startDate: formatISO(startDate, { representation: "date" }),
    endDate: formatISO(endDate, { representation: "date" })
  }).toString();

  const url = rootApiUrl + "/tabular/" + id + "?" + search;

  const headers = { "Accept": "application/json" };

  const resp = await fetch(url, { headers });
  if (!resp.ok) {
    throw new Error(`${resp.status} ${resp.statusText}`);
  }

  const json: TabularApiResponse = await resp.json();
  const { meta, data } = json;

  const measures = Object.keys(meta.units);
  switch (measures.length) {
    case 1:
      const measure = measures[0]
      const unit = meta.units[measure];

      if (!isLengthUnit(unit)) {
        throw new Error(`Unit ${unit} is not a length unit`);
      }

      const oneColumnRows = new Map(data.map((datum) =>
        [new Date(Date.parse(datum.ts)), datum[measure] as number]))

      const one: TabularData = { type: "OneColumn", unit, rows: oneColumnRows };
      return Promise.resolve(one);

    case 2:
      const stage = measures[0]
      const stageUnit = meta.units[stage];

      if (!isLengthUnit(stageUnit)) {
        throw new Error(`Unit ${stageUnit} is not a length unit`);
      }

      const discharge = measures[1]
      const dischargeUnit = meta.units[discharge];

      if (!isVolumeFlowRateUnit(dischargeUnit)) {
        throw new Error(`Unit ${dischargeUnit} is not a volume flow rate unit`);
      }

      const twoColumnRows = new Map(data.map((datum) =>
        [new Date(Date.parse(datum.ts)), [datum[stage], datum[discharge]] as [number, number]]))

      const two: TabularData = {
        type: "TwoColumns",
        units: [stageUnit, dischargeUnit],
        rows: twoColumnRows
      }
      return Promise.resolve(two);

    default:
      throw new Error(`Unhandled number of units ${meta.units.length}`);
  }
}

export default fetchTabular;